import { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  adminApi,
  type AdminRule,
  type AdminRuleInput,
  type AdminTarget,
  type RuleSource,
  type ScopeEnv,
  type Severity,
} from '../../api/admin';

const EMPTY: AdminRuleInput = {
  id: '',
  severity: 'warn',
  source: 'metrics',
  scope_env: 'any',
  condition: 'value > 80 for 5m',
  cooldown: '5m',
  targets: [],
};

export function RulesTab() {
  const { data: rules = [], mutate } = useSWR('admin/rules', () => adminApi.listRules());
  const { data: targets = [] } = useSWR<AdminTarget[]>('admin/targets', () =>
    adminApi.listTargets(),
  );
  const [editing, setEditing] = useState<AdminRuleInput | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(r: AdminRuleInput) {
    setErr(null);
    try {
      if (adding) await adminApi.createRule(r);
      else await adminApi.updateRule(r);
      await mutate();
      setEditing(null);
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(r: AdminRule) {
    if (!confirm(`Delete rule '${r.id}'?`)) return;
    setErr(null);
    try {
      await adminApi.deleteRule(r.id);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
          Alert rules ({rules.length})
        </h3>
        <button
          type="button"
          className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
          onClick={() => {
            setAdding(true);
            setEditing({ ...EMPTY });
          }}
        >
          + Add rule
        </button>
      </div>
      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Severity</th>
              <th className="px-3 py-2 text-left">Source / metric</th>
              <th className="px-3 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-left">Condition</th>
              <th className="px-3 py-2 text-left">Cooldown</th>
              <th className="px-3 py-2 text-left">Targets</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-cockpit-border">
                <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                <td
                  className={`px-3 py-2 text-xs uppercase ${r.severity === 'crit' ? 'text-cockpit-crit' : 'text-cockpit-warn'}`}
                >
                  {r.severity}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.source}
                  {r.metric && <span className="ml-1 text-cockpit-dim">/ {r.metric}</span>}
                </td>
                <td className="px-3 py-2 text-xs uppercase">{r.scope_env}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.condition}</td>
                <td className="px-3 py-2 text-xs">{r.cooldown}</td>
                <td className="px-3 py-2 text-xs">
                  {r.targets.join(', ') || <span className="text-cockpit-dim">none</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text"
                    onClick={() => {
                      setAdding(false);
                      setEditing({
                        id: r.id,
                        severity: r.severity,
                        source: r.source,
                        ...(r.metric ? { metric: r.metric } : {}),
                        scope_env: r.scope_env,
                        condition: r.condition,
                        cooldown: r.cooldown,
                        targets: r.targets,
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs text-cockpit-crit hover:underline"
                    onClick={() => void remove(r)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no rules yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <RuleForm
          rule={editing}
          targets={targets}
          isNew={adding}
          onSave={save}
          onCancel={() => {
            setEditing(null);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function RuleForm({
  rule,
  targets,
  isNew,
  onSave,
  onCancel,
}: {
  rule: AdminRuleInput;
  targets: AdminTarget[];
  isNew: boolean;
  onSave: (r: AdminRuleInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(rule);
  const [validation, setValidation] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await adminApi.parseCondition(form.condition);
        if (cancelled) return;
        if (res.ok) setValidation({ ok: true });
        else setValidation({ ok: false, error: res.error });
      } catch (e) {
        if (cancelled) return;
        setValidation({ ok: false, error: e instanceof Error ? e.message : String(e) });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form.condition]);

  const toggleTarget = (id: string) => {
    setForm((f) =>
      f.targets.includes(id)
        ? { ...f, targets: f.targets.filter((t) => t !== id) }
        : { ...f, targets: [...f.targets, id] },
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="space-y-3 rounded border border-cockpit-border bg-cockpit-panel p-4"
    >
      <h4 className="text-sm font-semibold">{isNew ? 'New alert rule' : `Edit ${rule.id}`}</h4>
      <div className="grid grid-cols-2 gap-3 text-xs lg:grid-cols-3">
        <Field label="ID (immutable)" htmlFor="id">
          <input
            id="id"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.id}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            required
          />
        </Field>
        <Field label="Severity" htmlFor="severity">
          <select
            id="severity"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.severity}
            onChange={(e) => setForm({ ...form, severity: e.target.value as Severity })}
          >
            <option value="warn">warn</option>
            <option value="crit">crit</option>
          </select>
        </Field>
        <Field label="Source" htmlFor="source">
          <select
            id="source"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.source}
            onChange={(e) =>
              setForm({
                ...form,
                source: e.target.value as RuleSource,
                ...(e.target.value === 'metrics' ? {} : { metric: undefined }),
              })
            }
          >
            <option value="metrics">metrics</option>
            <option value="service_states">service_states</option>
            <option value="health_checks">health_checks</option>
          </select>
        </Field>
        {form.source === 'metrics' && (
          <Field label="Metric name" htmlFor="metric">
            <input
              id="metric"
              className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
              value={form.metric ?? ''}
              onChange={(e) => setForm({ ...form, metric: e.target.value })}
              placeholder="cpu_pct"
              required
            />
          </Field>
        )}
        <Field label="Scope env" htmlFor="scope_env">
          <select
            id="scope_env"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            value={form.scope_env}
            onChange={(e) => setForm({ ...form, scope_env: e.target.value as ScopeEnv })}
          >
            <option value="any">any</option>
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
        </Field>
        <Field label="Cooldown" htmlFor="cooldown">
          <input
            id="cooldown"
            className="w-full rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text"
            value={form.cooldown}
            onChange={(e) => setForm({ ...form, cooldown: e.target.value })}
            placeholder="5m"
            required
          />
        </Field>
        <Field label="Condition" htmlFor="condition" colSpan={3}>
          <input
            id="condition"
            className={`w-full rounded border bg-cockpit-bg px-2 py-1 font-mono text-cockpit-text ${
              validation?.ok === false
                ? 'border-cockpit-crit'
                : validation?.ok
                  ? 'border-cockpit-ok'
                  : 'border-cockpit-border'
            }`}
            value={form.condition}
            onChange={(e) => setForm({ ...form, condition: e.target.value })}
            placeholder="value > 80 for 5m"
            required
          />
          {validation?.ok === false && (
            <span className="text-cockpit-crit">{validation.error}</span>
          )}
          {validation?.ok && <span className="text-cockpit-ok">parses OK</span>}
        </Field>
        <Field label="Targets" htmlFor="targets" colSpan={3}>
          <div className="flex flex-wrap gap-2">
            {targets.length === 0 && (
              <span className="text-cockpit-dim">
                no targets defined — add some in the Targets tab
              </span>
            )}
            {targets.map((t) => {
              const on = form.targets.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTarget(t.id)}
                  className={
                    on
                      ? 'rounded border border-cockpit-text bg-cockpit-text px-2 py-0.5 text-cockpit-bg'
                      : 'rounded border border-cockpit-border px-2 py-0.5 text-cockpit-dim hover:text-cockpit-text'
                  }
                >
                  {t.id} ({t.type})
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg disabled:opacity-40"
          disabled={validation?.ok === false}
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-cockpit-border px-3 py-1 text-xs text-cockpit-dim hover:text-cockpit-text"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
  colSpan = 1,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  colSpan?: 1 | 2 | 3;
}) {
  const cls =
    colSpan === 3
      ? 'col-span-3 flex flex-col gap-1'
      : colSpan === 2
        ? 'col-span-2 flex flex-col gap-1'
        : 'flex flex-col gap-1';
  return (
    <label htmlFor={htmlFor} className={cls}>
      <span className="text-cockpit-dim">{label}</span>
      {children}
    </label>
  );
}
